import React, { useState } from "react";
import { FullScreenPreloader } from "@/shared/common/layouts/FullScreenPreloader";
import { TeamConfig, useTeamConfig } from "../hooks/useTeamConfig";
import { useEffectAsync } from "@/shared/base/hooks/useEffectAsync";
import i18n, { ResourceLanguage } from "i18next";
import { initReactI18next } from "react-i18next";
import { constants } from "../config/constants";
import { useStorageCache } from "../hooks/useStorageCache";
import { api } from "../api/api";
import { createState } from "../store/zustand_store";
import { useRouterExtended } from "../hooks/useRouterExtended";
import parseHtml from "html-react-parser";

export const htmlToJsxProcessor = {
  name: "htmlToJsx",
  type: "postProcessor",
  process(value: string, key: string, options: any, translator: any) {
    if (typeof value === "string" && value.includes("<")) {
      try {
        return parseHtml(value);
      } catch (e) {
        console.warn("HTML parse error for key:", key, e);
        return value;
      }
    }
    return value;
  },
};

function formatTeamResourcesNS(teamConfig: TeamConfig) {
  const teamResources = teamConfig?.i18nConfig?.resources;
  if (!teamResources) return {};
  type NSType = Record<string, { translation: ResourceLanguage }>;
  const ns: NSType = {};

  Object.keys(teamResources).forEach((key) => {
    ns[key] = { translation: teamResources[key] };
  });

  return ns;
}

export const useI18nState = createState({
  locale: "",
});

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const router = useRouterExtended();
  const urlLng = router?.query?.lng as string;
  const teamConfig = useTeamConfig();
  const lngSelectionCache = useStorageCache(constants.languageSelectionStorageKey); // prettier-ignore
  const i18nState = useI18nState();
  const [isLoaded, setIsLoaded] = useState(false);

  const initI18n = () => {
    return new Promise((resolve) => {
      if (i18n.isInitialized) return resolve(true);

      const teamI18nConfig = teamConfig?.i18nConfig;
      if (!teamI18nConfig) lngSelectionCache?.delete(teamConfig?.teamSchema);
      const defaultLng = urlLng || lngSelectionCache?.get(teamConfig?.teamSchema) || teamI18nConfig?.lng || constants.locales.en; // prettier-ignore
      const formattedResources = formatTeamResourcesNS(teamConfig);

      i18n
        .use(initReactI18next)
        .use(htmlToJsxProcessor as any)
        .init({
          // debug: process.env.NODE_ENV === "development",
          interpolation: { escapeValue: false },
          saveMissing: false, // don’t send/save missing keys
          missingKeyHandler: () => {},
          parseMissingKeyHandler: (key: any) => null,
          returnObjects: true,
          returnNull: true,
          returnEmptyString: true,
          fallbackLng: constants.locales.en,
          ...teamI18nConfig,
          lng: defaultLng,
          resources: formattedResources,
          postProcess: ["htmlToJsx"],
        });

      lngSelectionCache?.set(teamConfig?.teamSchema, defaultLng);
      i18nState.set({ locale: defaultLng });
      return resolve(true);
    });
  };

  useEffectAsync({
    async effect() {
      try {
        await initI18n();
      } catch (error) {
        console.error("error initializing i18n", error);
      } finally {
        setIsLoaded(true);
      }
    },
  });

  useEffectAsync({
    deps: [i18nState?.locale],
    async effect() {
      if (!i18nState?.locale) return;
      if (!teamConfig?.i18nConfig) return;
      api.defaults.headers.common["Locale"] = i18nState.locale as any;
    },
  });

  if (!isLoaded) return <FullScreenPreloader />;
  return children;
}
